import { giphyBlack, giphyRed } from '@giphy/js-brand'
import React, { useContext, useState } from 'react'
import UserContext from 'shared/components/context/user'
import CreationToolRating from '../../../types/rating'
import styled from 'styled-components'
import CreationToolError from '../../../types/error'
import AlertModal from '../../alert-modal'
import { ColumnBlock, ContainerBlock, SplitBlock } from '../../blocks'
import Fields from '../shared/components/capture-info'
import { ICapture, ICustomFont, INewCaptureInfo, IWatermark } from '../shared/types'
import API from '../shared/utils/api'
import BaseModal from './base'
import DotsLoader from 'shared/components/loaders/dots'
import VideoPlayer from '../../video-player'
import { CreationToolSourceFile } from '../../../types'

const api = new API()

const LoaderContainer = styled.div`
    width: 100%;
    align-items: center;
    display: flex;
    justify-content: center;
`

const Container = styled(ContainerBlock)`
    position: relative;
    width: 1040px;
`

const Wrapper = styled(SplitBlock)`
    background: ${giphyBlack};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`

const PreviewWrapper = styled.div`
    padding-top: 30px;
    text-align: center;
`

type Props = {
    capture: ICapture
    preview: string
    updateCapture: (capture: ICapture) => void
    onClose: () => void
}

const CreationToolsEditCaptureModal = ({ capture, updateCapture, preview, onClose }: Props) => {
    const { user } = useContext(UserContext)
    const [error, setError] = useState<CreationToolError>()
    const [hasChanges, setHasChanges] = useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const [isUpdating, setIsUpdating] = useState<boolean>(false)

    const onSave = async (info: INewCaptureInfo) => {
        setIsUpdating(true)

        try {
            const response = await api.saveCaptureEdits(info, capture.id)

            if (response.status === 'error') {
                throw 'Unable to update capture'
            }
            updateCapture(response.capture)
        } catch (err) {
            setIsUpdating(false)
            setError(CreationToolError.SuperCutUpdateFailed)
        }

        onClose()
    }

    const onCloseClick = () => {
        if (isUpdating) {
            return
        }

        if (hasChanges) {
            setShowConfirmModal(true)
            return
        }

        onClose()
    }

    return (
        <BaseModal
            {...{
                error,
                isLoading: false,
                onClose: onCloseClick,
            }}
        >
            <Container style={showConfirmModal ? { display: 'none' } : {}}>
                {isUpdating ? (
                    <LoaderContainer>
                        <DotsLoader />
                    </LoaderContainer>
                ) : (
                    <>
                        <Wrapper>
                            <ColumnBlock>
                                <PreviewWrapper>
                                    <VideoPlayer
                                        {...{
                                            source: {
                                                hasAudio: false,
                                                width: capture.width,
                                                height: capture.height,
                                                source: preview,
                                            } as CreationToolSourceFile,
                                            width: 460,
                                        }}
                                    />
                                </PreviewWrapper>
                            </ColumnBlock>
                            <ColumnBlock>
                                <Fields
                                    {...{
                                        isUpdating,
                                        onSave,
                                        captureInfo: {
                                            title: capture.title,
                                            username: user?.username,
                                            sourceUrl: capture.upload_metadata?.source_url,
                                            font: capture.custom_font
                                                ? ({
                                                      id: 'custom_font',
                                                      title: 'Custom Font',
                                                      font: capture.custom_font,
                                                  } as ICustomFont)
                                                : undefined,
                                            tags: capture.upload_metadata?.tags.split(',').filter(i => i),
                                            watermark: capture.watermark
                                                ? ({
                                                      id: 'custom_watermark',
                                                      image: capture.watermark,
                                                      title: 'Custom Watermark',
                                                  } as IWatermark)
                                                : undefined,
                                            rating:
                                                (capture.upload_metadata?.rating as CreationToolRating) ||
                                                CreationToolRating.Unrated,
                                        },
                                        saveLabel: 'Update Capture',
                                        onUpdate: setHasChanges,
                                        onCancel: onCloseClick,
                                    }}
                                />
                            </ColumnBlock>
                        </Wrapper>
                    </>
                )}
            </Container>

            {showConfirmModal && (
                <AlertModal
                    {...{
                        stickerId: 'jOsoGmmWGSloPU8fMH',
                        title: 'You will lose your changes to this capture!',
                        primaryButtonLabel: 'Discard Changes',
                        primaryButtonColor: giphyRed,
                        secondaryButtonLabel: 'Nevermind',
                        onPrimaryButtonClick: onClose,
                        onSecondaryButtonClick: () => setShowConfirmModal(false),
                    }}
                />
            )}
        </BaseModal>
    )
}

export default CreationToolsEditCaptureModal
